import { useLocalStorage } from "@elit-hair/elit-tools";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Icon,
} from "@elit-hair/elit-ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChrome } from "react-icons/fa";
import { TbExternalLink } from "react-icons/tb";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { CHROME_EXTENSION_STORE_URL } from "@/features/chrome-extension/chrome-extension.constants";
import { useChromeExtensionStatus } from "@/features/chrome-extension/hooks/use-chrome-extension-status";
import { useCapacitor } from "@/hooks/use-capacitor";

function isChromiumBrowser() {
    return /\b(Chrome|CriOS|Edg)\//.test(navigator.userAgent);
}

export function ChromeExtensionInstallDialog() {
    const { t } = useTranslation();
    const { status } = useChromeExtensionStatus();
    const { isCapacitor } = useCapacitor();
    const [dismissed, setDismissed] = useState(false);
    const [doNotAskAgain, setDoNotAskAgain] = useLocalStorage<boolean>(
        LOCAL_STORAGE_KEYS.CHROME_EXTENSION_INSTALL_DO_NOT_ASK.key,
        LOCAL_STORAGE_KEYS.CHROME_EXTENSION_INSTALL_DO_NOT_ASK.default
    );
    const open = !doNotAskAgain && !dismissed && !isCapacitor && isChromiumBrowser() && status === "not_installed";

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                // Closing only hides it until the next visit; "Don't show again" hides it for good.
                if (!nextOpen) setDismissed(true);
            }}
        >
            {/* No focus on open, so the "Don't show again" link does not show a focus ring. */}
            <DialogContent className="max-w-lg" onOpenAutoFocus={(event) => event.preventDefault()}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Icon icon={FaChrome} />
                        {t("chrome_extension_required_title")}
                    </DialogTitle>
                    <DialogDescription>{t("chrome_extension_required_description")}</DialogDescription>
                </DialogHeader>

                <p className="whitespace-pre-line text-sm text-muted-foreground">
                    {t("chrome_extension_install_tutorial")}
                </p>

                <DialogFooter className="items-center sm:justify-between">
                    <Button
                        variant="link"
                        size="sm"
                        className="h-auto px-0 text-xs text-muted-foreground"
                        onClick={() => {
                            setDoNotAskAgain(true);
                            setDismissed(true);
                        }}
                    >
                        {t("dont_show_again")}
                    </Button>
                    <Button asChild>
                        <a href={CHROME_EXTENSION_STORE_URL} target="_blank" rel="noopener noreferrer">
                            <Icon icon={TbExternalLink} className="mr-2" />
                            {t("open_chrome_web_store")}
                        </a>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
